import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  getPostsByCategorySlug,
  getCategories,
  getCategorySeo,
} from "@/content/blog";
import Seo from "@/lib/Seo";

export default function BlogCategory() {
  const { slug } = useParams();
  const categories = getCategories();
  const cat = categories.find((c) => c.slug === slug);

  if (!cat) return <Navigate to="/blog" replace />;

  const posts = getPostsByCategorySlug(slug);
  const seo = getCategorySeo(slug, cat.name);
  const otherCats = categories.filter((c) => c.slug !== slug);

  // CollectionPage JSON-LD so Google understands this is a topic hub.
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seo.seoTitle,
    description: seo.seoDescription,
    url: `https://www.steadypathfunding.com/blog/category/${slug}`,
    hasPart: posts.map((p) => ({
      "@type": "Article",
      headline: p.title,
      url: `https://www.steadypathfunding.com/blog/${p.slug}`,
      image: p.image,
    })),
  };

  return (
    <div>
      <Seo
        path={`/blog/category/${slug}`}
        title={seo.seoTitle}
        description={seo.seoDescription}
        jsonLd={collectionLd}
      />

      {/* Editorial hero */}
      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60 hover:text-[color:var(--gold)] transition-colors mb-10"
            >
              <ArrowLeft size={14} /> The full journal
            </Link>
            <div className="overline">Category · {posts.length} field note{posts.length === 1 ? "" : "s"}</div>
            <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight">
              {seo.heading.split(" ").slice(0, -1).join(" ")}{" "}
              <em className="italic text-[color:var(--gold)]">
                {seo.heading.split(" ").slice(-1)}
              </em>
            </h1>
            <div className="mt-10 h-px w-12 bg-[color:var(--gold)]" />
            <p className="mt-10 max-w-2xl text-[color:var(--bone)]/75 leading-relaxed text-lg">
              {seo.tagline}
            </p>
          </div>
          {seo.heroImage && (
            <div className="col-span-12 md:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={seo.heroImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="category-hero-image"
                />
                <div className="absolute inset-0 bg-black/12" />
                <div className="absolute bottom-6 left-6 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                  {cat.name}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Article grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: (i % 6) * 0.05 }}
                className="col-span-12 md:col-span-6 lg:col-span-4 border-t border-black/10 pt-8"
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`category-card-${p.slug}`}
                  className="group block"
                >
                  <div className="relative aspect-[4/3] overflow-hidden mb-6">
                    <img
                      src={p.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-black/15" />
                  </div>
                  <div className="inline-flex items-center px-2.5 py-1 border border-[color:var(--bone)]/20 rounded-full font-mono text-[9px] tracking-[0.22em] uppercase text-[color:var(--bone)]/60">
                    {p.readTime}
                  </div>
                  <h3 className="mt-4 font-serif text-2xl md:text-3xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[color:var(--bone)]/70 text-sm leading-relaxed">
                    {p.excerpt}
                  </p>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Other categories */}
      {otherCats.length > 0 && (
        <section className="py-20 border-t border-black/10 bg-[color:var(--surface)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="overline mb-8">Also on the journal</div>
            <div className="grid grid-cols-12 gap-4">
              {otherCats.map((c) => (
                <Link
                  key={c.slug}
                  to={`/blog/category/${c.slug}`}
                  data-testid={`related-category-${c.slug}`}
                  className="col-span-12 sm:col-span-6 lg:col-span-3 group border border-black/10 bg-[color:var(--bg-elev)] p-6 hover:border-[color:var(--gold)] transition-colors"
                >
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {c.count} article{c.count === 1 ? "" : "s"}
                  </div>
                  <div className="mt-3 font-serif text-2xl group-hover:text-[color:var(--gold)] transition-colors flex items-center gap-2">
                    {c.name}
                    <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
